import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient, queryKeys, subscribeToBriefsBroadcast } from './lib/react-query.ts';
import { ArticleBriefProvider } from './contexts/ArticleBriefContext.tsx';
import { ArticleSectionBySection } from './components/ArticleSectionBySection.tsx';
import { DeepResearchFlow } from './components/DeepResearchFlow.tsx';
import { ManualSourceFlow } from './components/ManualSourceFlow.tsx';
import { Toaster } from './components/ui/toaster.tsx';
import { ErrorBoundary } from './components/ErrorBoundary';
import LandingPage from './components/LandingPageV2.tsx';
import { AppHeader } from './components/AppHeader.tsx';
import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from '@clerk/clerk-react';
import { AdminRoute } from './components/AdminRoute';
import './index.css';

// Lazy load route components for code splitting
const ArticleBriefManager = lazy(() => import('./components/ArticleBriefManager.tsx').then(m => ({ default: m.ArticleBriefManager })));
const ArticleBriefDetailPage = lazy(() => import('./components/ArticleBriefDetailPage.tsx').then(m => ({ default: m.ArticleBriefDetailPage })));
const InsightsPage = lazy(() => import('./pages/InsightsPage.tsx').then(m => ({ default: m.InsightsPage })));
const OutlinePage = lazy(() => import('./features/outline/components/OutlinePage.tsx').then(m => ({ default: m.OutlinePage })));
const ExportPage = lazy(() => import('./components/ExportPage.tsx').then(m => ({ default: m.ExportPage })));
const StyleGuideManager = lazy(() => import('./components/StyleGuideManager.tsx').then(m => ({ default: m.StyleGuideManager })));
const StyleGuideForm = lazy(() => import('./components/StyleGuideForm.tsx').then(m => ({ default: m.StyleGuideForm })));
const ConfigSettingsPage = lazy(() => import('./pages/ConfigSettingsPage.tsx').then(m => ({ default: m.ConfigSettingsPage })));
const ContentInspirationPage = lazy(() => import('./pages/ContentInspirationPage.tsx').then(m => ({ default: m.ContentInspirationPage })));
const OutlineGuideManager = lazy(() => import('./features/outline/components/OutlineGuideManager.tsx').then(m => ({ default: m.OutlineGuideManager })));
const OutlineGuideForm = lazy(() => import('./features/outline/components/OutlineGuideForm.tsx').then(m => ({ default: m.OutlineGuideForm })));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy.tsx').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService.tsx').then(m => ({ default: m.TermsOfService })));
const ComingSoon = lazy(() => import('./pages/ComingSoon.tsx').then(m => ({ default: m.ComingSoon })));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-lg text-muted-foreground">Loading...</div>
  </div>
);

// Wrapper to conditionally show AppHeader (hide on landing page when signed out
// and on public static pages that bring their own header)
const STATIC_PAGE_PATHS = new Set(['/legal/privacy', '/legal/terms', '/coming-soon']);

function useShowAppHeader() {
  const location = useLocation();
  const { isSignedIn } = useAuth();

  if (location.pathname === '/' && !isSignedIn) return false;
  if (STATIC_PAGE_PATHS.has(location.pathname)) return false;
  return true;
}

function ConditionalAppHeader() {
  return useShowAppHeader() ? <AppHeader /> : null;
}

function ContentArea({ children }: Readonly<{ children: React.ReactNode }>) {
  const showHeader = useShowAppHeader();
  return (
    <div className={`flex-1 overflow-auto ${showHeader ? 'pt-[var(--app-header-height)]' : ''}`}>
      {children}
    </div>
  );
}

function App() {
  useEffect(() => {
    const unsubscribe = subscribeToBriefsBroadcast(() => {
      queryClient.invalidateQueries({ queryKey: queryKeys.brief.list() });
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ArticleBriefProvider>
        <Router>
          <div className="theme h-screen flex flex-col bg-background">
            {/* Modern Header - hidden on landing page for signed-out users */}
            <ConditionalAppHeader />
            <ContentArea>
              <ErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                  <Route path="/" element={
                    <>
                      <SignedOut><LandingPage /></SignedOut>
                      <SignedIn><Navigate to="/dashboard" replace /></SignedIn>
                    </>
                  } />
                  <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                  <Route path="/legal/terms" element={<TermsOfService />} />
                  <Route path="/coming-soon" element={<ComingSoon />} />
                  <Route path="/dashboard" element={
                    <>
                      <SignedIn><ArticleBriefManager /></SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/inspiration" element={
                    <>
                      <SignedIn><ContentInspirationPage /></SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/brief/:id" element={
                    <>
                      <SignedIn><ArticleBriefDetailPage /></SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/insights/:id" element={
                    <>
                      <SignedIn><InsightsPage /></SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/outline/:id" element={
                    <>
                      <SignedIn><OutlinePage /></SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/article/:id?" element={
                    <>
                      <SignedIn><ArticleSectionBySection /></SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/export/:id" element={
                    <>
                      <SignedIn><ExportPage /></SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route
                    path="/research/deep"
                    element={
                      <>
                        <SignedIn>
                          <div className="p-6">
                            <DeepResearchFlow onBack={() => window.history.back()} />
                          </div>
                        </SignedIn>
                        <SignedOut><RedirectToSignIn /></SignedOut>
                      </>
                    }
                  />
                  <Route
                    path="/research/manual"
                    element={
                      <>
                        <SignedIn>
                          <div className="p-6">
                            <ManualSourceFlow onBack={() => window.history.back()} />
                          </div>
                        </SignedIn>
                        <SignedOut><RedirectToSignIn /></SignedOut>
                      </>
                    }
                  />
                  <Route path="/style-guides" element={
                    <>
                      <SignedIn>
                        <AdminRoute>
                          <StyleGuideManager />
                        </AdminRoute>
                      </SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/style-guides/new" element={
                    <>
                      <SignedIn>
                        <AdminRoute>
                          <StyleGuideForm />
                        </AdminRoute>
                      </SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                   <Route path="/style-guides/:id/edit" element={
                    <>
                      <SignedIn>
                        <AdminRoute>
                          <StyleGuideForm />
                        </AdminRoute>
                      </SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/outline-templates" element={
                    <>
                      <SignedIn>
                        <AdminRoute>
                          <OutlineGuideManager />
                        </AdminRoute>
                      </SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/outline-templates/new" element={
                    <>
                      <SignedIn>
                        <AdminRoute>
                          <OutlineGuideForm />
                        </AdminRoute>
                      </SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/outline-templates/:id/edit" element={
                    <>
                      <SignedIn>
                        <AdminRoute>
                          <OutlineGuideForm />
                        </AdminRoute>
                      </SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  <Route path="/settings/config" element={

                    <>
                      <SignedIn>
                        <AdminRoute>
                          <ConfigSettingsPage />
                        </AdminRoute>
                      </SignedIn>
                      <SignedOut><RedirectToSignIn /></SignedOut>
                    </>
                  } />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
              <Toaster />
            </ContentArea>
          </div>
        </Router>

        {/* React Query DevTools - only in development */}
        {(import.meta as any).env?.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </ArticleBriefProvider>
    </QueryClientProvider>
  );
}



export default App;
